import {reportBug, reportBugOnce} from '@packages/utilities/reporter';
import {Plugins, ElementPreset} from '@packages/systems/wfdl';
import {Qualified, getPluginName} from '@packages/systems/wfdl-Qualified';
import {ElementCodeCompIcon} from '@packages/systems/spring';
import type {
  LibraryVersionedResource,
  LibraryManifest,
  LibraryExport,
} from './types';
import {LIBRARY_EXPORT_TYPES, LIBRARY_STATUS} from './constants';

/**
 * Parse the Library Plugin Resource and reconstruct the LibraryManifest
 */
export const libraryManifestFromResource = (
  resource: LibraryVersionedResource,
  opts?: {status?: LIBRARY_STATUS}
): LibraryManifest<string> => {
  const {status = LIBRARY_STATUS.installed} = opts || {};
  return {
    pluginId: resource.id,
    profileId: resource.profileId,
    overridesProfileId: resource.overridesProfileId,
    displayName: resource.displayName,
    displayIcon:
      resource.type === 'CODE_LIBRARY' ? ElementCodeCompIcon : undefined,
    traits: resource.traits,
    version: resource.version,
    installer: resource.installer,
    isLocalOverride: resource.isLocalOverride,
    publisher: resource.publisher,
    publisherImg: resource.publisherImg,
    isExternal: isLibraryExternal(resource),
    exports: getLibraryExports(resource),
    componentsCount: resource.componentsCount || 0,
    variablesCount: resource.variablesCount || 0,
    assetsCount: resource.assetsCount || 0,
    codeComponentsCount: resource.codeComponentsCount || 0,
    tagStylesCount: resource.tagStylesCount || 0,
    fontsCount: resource.fontsCount || 0,
    sourceFilesCount: resource.sourceFilesCount || 0,
    skillsCount: resource.skillsCount || 0,
    rulesCount: resource.rulesCount || 0,
    datePublished: resource.datePublished || null,
    description: resource.description || null,
    type: resource.type,
    source: resource.source,
    sourceSite: resource.sourceSite,
    profileStatus: resource.profileStatus,
    libraryCompilerVersion: resource.libraryCompilerVersion,
    status,
    librarySettings: resource.librarySettings,
  };
};

/**
 * - Built-in (internal) libraries use PrivilegedPlugin
 * - External libraries use (unprivileged) Plugin
 */
const isLibraryExternal = (resource: LibraryVersionedResource): boolean => {
  if (resource.plugin.type !== 'CONTENT') {
    // If the plugin is not loaded we can assume it's an external library
    return true;
  }
  return resource.plugin.value.type === 'PLUGIN';
};

/**
 * Parses all library export items and validates
 * they exist on the plugin
 */
const getLibraryExports = (
  resource: LibraryVersionedResource
): LibraryExport<string>[] => {
  const {exports: libraryExports, plugin} = resource;

  if (plugin.type !== 'CONTENT') {
    return libraryExports;
  }

  // Extract objects from plugin definition
  const {
    elementPresets = {},
    styleVariables = {},
    components = {},
    assets = {},
    styleBlocks = {},
  } = plugin.value.definition;

  // We iterate over the library exports and make sure
  // they actually exist in the plugin
  return libraryExports.filter((exportItem) => {
    if (
      exportItem.type === LIBRARY_EXPORT_TYPES.LAYOUT &&
      elementPresets[exportItem.layoutMetadata.targetComponentId]
    ) {
      return true;
    }

    if (
      // Currently, logic is dependent on the `elementPresetId`
      // matching the `componentId`
      exportItem.type === LIBRARY_EXPORT_TYPES.COMPONENT &&
      components[exportItem.metadata.targetComponentId] &&
      elementPresets[exportItem.metadata.targetComponentId]
    ) {
      return true;
    }
    if (
      exportItem.type === LIBRARY_EXPORT_TYPES.VARIABLE &&
      styleVariables[exportItem.metadata.targetVariableId]
    ) {
      return true;
    }
    if (
      exportItem.type === LIBRARY_EXPORT_TYPES.ASSET &&
      assets[exportItem.metadata.targetId]
    ) {
      return true;
    }
    if (
      exportItem.type === LIBRARY_EXPORT_TYPES.TAG_STYLE &&
      styleBlocks[exportItem.metadata.targetStyleBlockId]
    ) {
      return true;
    }
    if (exportItem.type === LIBRARY_EXPORT_TYPES.FONT) {
      // Export entry is metadata-only — drives counts, the changelog,
      // and picker grouping — so nothing to verify against the plugin here.
      return true;
    }
    if (exportItem.type === LIBRARY_EXPORT_TYPES.SOURCE_FILE) {
      return true;
    }

    const itemId =
      exportItem.type === LIBRARY_EXPORT_TYPES.LAYOUT
        ? exportItem.layoutMetadata.targetComponentId
        : exportItem.type === LIBRARY_EXPORT_TYPES.COMPONENT
          ? exportItem.metadata.targetComponentId
          : exportItem.type === LIBRARY_EXPORT_TYPES.VARIABLE
            ? exportItem.metadata.targetVariableId
            : exportItem.type === LIBRARY_EXPORT_TYPES.ASSET
              ? exportItem.metadata.targetId
              : exportItem.type === LIBRARY_EXPORT_TYPES.TAG_STYLE
                ? exportItem.metadata.targetStyleBlockId
                : '';

    reportBug(
      '[Libraries][getLibraryExports]',
      `Library export item [${exportItem.type}: ${itemId}] not found in plugin [${resource.id}]`,
      {
        team: 'structure',
        ticket: 'https://webflow.atlassian.net/browse/SCALE-154',
      }
    );
    return false;
  }, []);
};

/**
 * Here we just manually generate an array of `LibraryExport`s which we will use
 * to effectively mock/simulate the installation of a built-in Webflow library
 * that already has a plugin.
 */
export const getLibraryExportsFromPlugin = (
  plugin: Plugins.PluginDeclarations
): LibraryExport<string>[] => {
  const {elementPresets = {}} = plugin.definition;
  return Object.keys(elementPresets).reduce((libExports, elementPresetId) => {
    const preset = elementPresets[elementPresetId];
    if (!preset) {
      return libExports;
    }

    const label = ElementPreset.getLabel(preset);
    const category = ElementPreset.getCategory(preset);
    const previewImageURL = ElementPreset.getPrebuiltLayoutsImage(preset);
    // TODO - How to handle no preview image URL being available
    if (!previewImageURL) return libExports;

    libExports.push({
      type: LIBRARY_EXPORT_TYPES.LAYOUT,
      layoutMetadata: {
        // FIXME: Here I'm assuming that the root element ID matches the key of
        // the element preset, but this isn't something we should rely on:
        targetComponentId: elementPresetId,
        displayName: label,
        category: typeof category === 'string' ? [category] : [],
        previewImage: {
          // FIXME: What should we put here for id?
          id: '',
          url: previewImageURL || 'https://placekitten.com/g/200/200',
        },
      },
    });

    return libExports;
  }, [] as LibraryExport<string>[]);
};

/**
 * Simple util to determine if a Library origin is readonly
 */
export const isLibraryOrigin = (
  origin: Qualified<string> | null,
  libraries: {[id: string]: LibraryManifest<string>}
) => {
  if (!origin) {
    return false;
  }
  const pluginId = getPluginName(origin);
  const libraryManifest = libraries[pluginId];
  if (!libraryManifest) {
    reportBugOnce(
      '[Libraries][isLibraryOrigin]',
      `Library manifest not found for library: [${pluginId}]`,
      {
        team: 'structure',
        ticket: 'https://webflow.atlassian.net/browse/SCALE-39',
      }
    );
    // This is probably a component from a library that was uninstalled or
    // updated to a new version and something went wrong while updating the site data
    return true;
  }

  // For now we can assume all libraries are readonly
  // if they have an 'origin' set. We might need to change this
  // once we consolidate WS and Marketplace libraries.
  return true;
};
